export type RoomRole = 'guest' | 'member' | 'moderator' | 'owner';

export const ROLE_RANK: Record<RoomRole, number> = { guest: 0, member: 1, moderator: 2, owner: 3 };

export type RoomAction =
  | 'listen'
  | 'chat'
  | 'vote'
  | 'dj'
  | 'moderate' // delete chat, mute, kick, ban, skip, remove DJ
  | 'settings'
  | 'invites'
  | 'assign_moderators'
  | 'archive';

const REQUIRED: Record<RoomAction, RoomRole> = {
  listen: 'guest',
  chat: 'member',
  vote: 'member',
  dj: 'member',
  moderate: 'moderator',
  settings: 'owner',
  invites: 'owner',
  assign_moderators: 'owner',
  archive: 'owner',
};

export function can(role: RoomRole, action: RoomAction): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[REQUIRED[action]];
}

/**
 * May `actor` take a moderation action against `target`? Strictly higher rank is required, so
 * moderators cannot act on other moderators or owners, and nobody can act on themselves.
 * Platform admins are handled separately (they act through the admin surface).
 */
export function canModerateTarget(
  actor: { userId: string; role: RoomRole },
  target: { userId: string; role: RoomRole },
): boolean {
  if (actor.userId === target.userId) return false;
  if (!can(actor.role, 'moderate')) return false;
  return ROLE_RANK[actor.role] > ROLE_RANK[target.role];
}

/** Only owners assign roles, never to themselves, and only between member and moderator. */
export function canAssignRole(
  actor: { userId: string; role: RoomRole },
  target: { userId: string; role: RoomRole },
  newRole: RoomRole,
): boolean {
  if (actor.userId === target.userId) return false;
  if (actor.role !== 'owner') return false;
  if (target.role === 'owner') return false;
  return newRole === 'moderator' || newRole === 'member';
}
